import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Crown, Edit2, Gavel } from "lucide-react";

export default function Profile() {
  const { userId } = useParams();
  const { user: me, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");

  const load = () => api.get(`/users/${userId}`).then((r) => {
    setData(r.data);
    setName(r.data.user.name);
    setBio(r.data.user.bio || "");
  });
  useEffect(() => { load(); }, [userId]);

  const save = async () => {
    const { data: u } = await api.put("/auth/me", { name, bio });
    setUser(u); setEdit(false); load();
  };

  if (!data) return <div className="p-8" data-testid="profile-loading">Loading...</div>;
  const { user, threads } = data;
  const isMe = me?.id === user.id;

  return (
    <div className="pastel-grain min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="brutal-border brutal-shadow rounded-2xl p-6 sm:p-8"
          style={{ background: "var(--secondary)" }}>
          <div className="flex items-start gap-5">
            <div className="w-20 h-20 sm:w-24 sm:h-24 brutal-border rounded-2xl grid place-items-center font-display text-3xl font-black bg-white"
              data-testid="profile-avatar">
              {user.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1">
              {edit ? (
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input"
                  className="h-11 brutal-border rounded-xl font-bold text-2xl bg-white focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
              ) : (
                <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">{user.name}</h1>
              )}
              <div className="text-sm text-zinc-700 mt-1 font-medium">{user.email}</div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {user.membership !== "free" && (
                  <span className="sticker" style={{ background: "var(--accent)" }}>
                    <Crown size={12} strokeWidth={2.5} /> {user.membership}
                  </span>
                )}
                {user.role === "admin" && (
                  <span className="sticker" style={{ background: "var(--pink)" }}>Admin</span>
                )}
                {user.role === "moderator" && (
                  <span className="sticker" style={{ background: "var(--green)" }}>
                    <Gavel size={12} strokeWidth={2.5} /> Moderator
                  </span>
                )}
              </div>
            </div>
            {isMe && (
              <Button onClick={() => edit ? save() : setEdit(true)}
                data-testid={edit ? "profile-save-btn" : "profile-edit-btn"}
                className="h-10 brutal-border brutal-shadow-sm rounded-xl font-bold gap-2 bg-white hover:bg-white text-[var(--ink)]">
                <Edit2 size={14} /> {edit ? "Save" : "Edit"}
              </Button>
            )}
          </div>
          <div className="mt-5">
            {edit ? (
              <Textarea value={bio} onChange={(e) => setBio(e.target.value)} data-testid="profile-bio-input"
                placeholder="Tell us about yourself..."
                className="brutal-border rounded-xl bg-white font-medium focus-visible:ring-0 focus-visible:border-[var(--ink)]" rows={3} />
            ) : (
              <p className="font-medium text-zinc-800" data-testid="profile-bio">{user.bio || (isMe ? "Add a short bio." : "No bio.")}</p>
            )}
          </div>
        </div>

        <div className="mt-8">
          <h2 className="font-display text-2xl font-bold mb-3">Threads ({threads.length})</h2>
          <div className="space-y-3">
            {threads.length === 0 && <p className="text-zinc-500 font-medium">No threads yet.</p>}
            {threads.map((t) => (
              <Link key={t.id} to={`/thread/${t.id}`} data-testid={`profile-thread-${t.id}`}
                className="block brutal-border brutal-shadow-sm rounded-xl p-4 bg-white brutal-hoverable">
                <div className="sticker text-[10px]" style={{ background: "var(--accent)" }}>{t.category_name}</div>
                <h3 className="font-display font-bold mt-2">{t.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
